/**
 * Tashkeela design tokens — the single source of truth for the palette.
 * Identity (2026-06-14 rebrand, see App Colors.png): a deep blue-black canvas
 * with a NEON accent — cyan `#00E1FF` lead, magenta→cyan brand gradient, violet
 * `#8A2BE2` and electric blue `#0060FF` supports. Captain gold stays gold-only.
 * The accent surface area is small + translucent (eye-comfortable, no loud
 * solid blocks). Token KEYS are stable (volt* = the accent family) so both
 * `tailwind.config.js` (Node require) and typed code (`src/config/theme.ts`)
 * consume the exact same values; remapping hues here re-themes the whole app.
 */
const colors = {
  deep: '#05050E', // app canvas base (gradient bottom)
  surface: '#111124', // solid cards (legacy; prefer `glass` below)
  surface2: '#1A1A38', // elevated / sheets / modals
  inset: '#0A0A1A', // inset wells (segmented track, toggles)
  line: 'rgba(255,255,255,0.08)', // hairline borders
  text: '#F1F2F8',
  muted: '#9698BE', // secondary text (blue-grey)
  soft: '#C6C9E6', // tertiary text
  inactive: '#6B6E94', // inactive tab icons

  // Neon accent — cyan lead (brand ACCENT #00E1FF), kept comfortable: large
  // fills use the translucent glass-cyan below, only small accents are solid
  // (live dot, numbers, thin borders, active pills).
  volt: '#22D3FF', // accent (cyan)
  voltBright: '#5FE9FF', // accent text / numbers on dark
  voltDim: '#1AA0C8', // pressed / secondary accent
  onVolt: '#04121A', // dark text on a solid-accent chip
  voltLine: 'rgba(34,211,255,0.30)', // accent borders
  voltGlass: 'rgba(34,211,255,0.12)', // primary CTA fill (calm, readable)
  voltGlassStrong: 'rgba(34,211,255,0.20)', // active tab / pressed glass

  // Brand gradient hues (the logo): magenta → cyan, with violet + blue.
  brandViolet: '#8A2BE2',
  brandMagenta: '#D600FF',
  brandBlue: '#0060FF',
  brandCyan: '#00E1FF',

  captain: '#F4C84B', // captain badge ONLY (gold)
  live: '#22D3FF', // live = accent cyan
  teamAway: '#FB7185', // AWAY team in two-team match viz (coral) — home stays volt/cyan; distinct from cyan, gold & the green pitch
  // win/draw/loss are SEMANTIC — kept green/grey/red (never recolour to accent).
  win: '#34D399',
  draw: '#9698BE',
  loss: '#FB7185',
  danger: '#FB7185',
  pitch: '#1F6B3A', // formation / lineup / TOTR pitch base — broadcast grass green (floodlit, deep, not neon)
  pitchLine: 'rgba(255,255,255,0.55)', // crisp white pitch markings

  // Glass surfaces — translucent over the gradient canvas (backdrop-blurred on
  // web). The premium, eye-comfortable replacement for flat solid cards.
  glass: 'rgba(20,20,42,0.55)', // standard card
  glass2: 'rgba(28,28,56,0.72)', // elevated glass (sheets, modals)
  glassBorder: 'rgba(255,255,255,0.10)', // glass edge
  glassHi: 'rgba(255,255,255,0.06)', // top highlight

  // App background gradient stops (top → bottom): a deep violet-blue-black.
  gradTop: '#100E28',
  gradMid: '#0A0A18',
  gradBottom: '#05050E',
};

// Light scheme: a clean, premium daylight palette that KEEPS the cyan identity
// (just deepened for contrast on white) and the gold/green/red semantics. Only
// the surfaces (now light), text tones (now dark) and glass/gradients flip; the
// accent + brand + semantic hues stay recognisably Tashkeela. Used by the
// runtime ThemeProvider (inline colours) and mirrored as CSS vars in global.css
// (Tailwind classes). KEYS match `colors` exactly so either object is a palette.
const lightColors = {
  deep: '#EEF1F8',
  surface: '#FFFFFF',
  surface2: '#F4F6FC',
  inset: '#E7EBF5',
  line: 'rgba(13,15,30,0.12)',
  text: '#0D0F1E',
  muted: '#5A5E80',
  soft: '#3A3D55',
  inactive: '#9A9DBB',

  volt: '#0FA8CE',
  voltBright: '#0883A6',
  voltDim: '#0B7C9A',
  onVolt: '#FFFFFF',
  voltLine: 'rgba(15,168,206,0.35)',
  voltGlass: 'rgba(15,168,206,0.12)',
  voltGlassStrong: 'rgba(15,168,206,0.20)',

  brandViolet: '#8A2BE2',
  brandMagenta: '#D600FF',
  brandBlue: '#0060FF',
  brandCyan: '#00C8E6',

  captain: '#C28A00',
  live: '#0FA8CE',
  teamAway: '#E11D48', // AWAY team in two-team match viz (deep rose for daylight contrast)
  win: '#0E9F6E',
  draw: '#5A5E80',
  loss: '#E11D48',
  danger: '#E11D48',
  pitch: '#2E8B4E', // daylight grass green — fresher / brighter than the dark theme
  pitchLine: 'rgba(255,255,255,0.70)', // crisp white pitch markings

  glass: 'rgba(255,255,255,0.72)',
  glass2: 'rgba(255,255,255,0.88)',
  glassBorder: 'rgba(13,15,30,0.10)',
  glassHi: 'rgba(255,255,255,0.55)',

  gradTop: '#F4F6FC',
  gradMid: '#EEF1F8',
  gradBottom: '#E7EBF5',
};

// `colors` stays the dark default so every existing static import is unchanged.
module.exports = { colors, darkColors: colors, lightColors };
